import React, { useMemo } from 'react';
import { getCoreRowModel, getPaginationRowModel, useReactTable, type PaginationState, type Updater } from '@tanstack/react-table';
import { Box, Button, Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconFileDownload, IconCloudUpload, IconRefresh, IconFilter, IconFilterFilled } from '@tabler/icons-react';
import Tabla from '../../../globales/componentes/tabla/tabla';
import { obtenerColumnasLogistica, type LogisticaCatalogos, type LogisticaTableMeta } from './constantesTablaLogistica';
import type { IConfiguracionLogistica, IFiltroLogistica } from '../interfaces';
import { FiltroLogisticaVista } from './filtroLogisticaVista';

interface TablaLogisticaVistaProps {
  datos: IConfiguracionLogistica[];
  /** Paginación en el back: datos trae solo la página actual. Sin ella se pagina en el front. */
  paginacion?: { indicePagina: number; tamanioPagina: number; total: number; alCambiarPagina: (pagina: number) => void };
  alDescargarPlantilla?: () => void;
  alCargaMasiva?: () => void;
  /** Vuelve a consultar la matriz (descarta una edición en curso). */
  alRecargar?: () => void;
  actualizarFila: (id: string, campo: keyof IConfiguracionLogistica | 'Cantidad', valor: string | number, indiceCantidad?: number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (id: string) => void;
  cancelarEdicion?: () => void;
  
  agregarFila: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
  catalogos: LogisticaCatalogos;
  /** Filtros por producto y máquina (se muestran con el ícono de filtro, como en las demás tablas). */
  filtros?: {
    habilitados: boolean;
    alAlternar: () => void;
    filtro: IFiltroLogistica;
    alCambiarFiltro: (campo: keyof IFiltroLogistica, valor: number | undefined) => void;
    alConsultar: () => void;
    alLimpiar: () => void;
  };
}

export const TablaLogisticaVista: React.FC<TablaLogisticaVistaProps> = ({
  datos,
  paginacion,
  alDescargarPlantilla,
  alCargaMasiva,
  alRecargar,
  actualizarFila,
  eliminarFila,
  guardarFila,
  filaEnEdicion,
  iniciarEdicion,
  cancelarEdicion,
  agregarFila,
  mostrarNotificacionObligatorio,
  intentoGuardar,
  catalogos,
  filtros
}) => {

  // Las columnas se definen una sola vez (para 12 cantidades por defecto)
  const columnas = useMemo(() => obtenerColumnasLogistica(12, catalogos), [catalogos]);

  const meta: LogisticaTableMeta = {
    actualizarFila,
    eliminarFila,
    guardarFila,
    filaEnEdicion,
    iniciarEdicion,
    cancelarEdicion,
    mostrarNotificacionObligatorio,
    intentoGuardar
  };

  const tabla = useReactTable({
    data: datos,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.Id,
    meta,
    ...(paginacion
      ? {
          manualPagination: true,
          pageCount: Math.max(1, Math.ceil(paginacion.total / paginacion.tamanioPagina)),
          state: { pagination: { pageIndex: paginacion.indicePagina, pageSize: paginacion.tamanioPagina } },
          onPaginationChange: (updater: Updater<PaginationState>) => {
            const actual = { pageIndex: paginacion.indicePagina, pageSize: paginacion.tamanioPagina };
            const nuevo = typeof updater === 'function' ? updater(actual) : updater;
            if (nuevo.pageIndex !== actual.pageIndex) paginacion.alCambiarPagina(nuevo.pageIndex);
          }
        }
      : { getPaginationRowModel: getPaginationRowModel(), initialState: { pagination: { pageIndex: 0, pageSize: 15 } } })
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" c="dimmed">
          Tabla Matriz de Puntajes de Espacio por Combinación Técnica
        </Text>
        <Group gap="sm">
          {/* Íconos con el estilo estándar de las tablas de los demás módulos (Tareas, Equipos, Clientes). */}
          <Group gap={4}>
            {alRecargar && (
              <Tooltip zIndex={100000} label="Refrescar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alRecargar}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip zIndex={100000} label="Descargar Plantilla" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alDescargarPlantilla} disabled={!alDescargarPlantilla}>
                <IconFileDownload size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip zIndex={100000} label="Carga Masiva" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alCargaMasiva} disabled={!alCargaMasiva}>
                <IconCloudUpload size={16} />
              </ActionIcon>
            </Tooltip>
            {filtros && (
              <Tooltip zIndex={100000} label="Filtrar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={filtros.alAlternar}>
                  {filtros.habilitados ? <IconFilterFilled size={16} /> : <IconFilter size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus color="var(--mantine-color-primary-0)" size={12} />} onClick={agregarFila} disabled={filaEnEdicion !== null && filaEnEdicion !== undefined}><Text fw={500} size="xs">Agregar Fila</Text></Button>
        </Group>
      </Group>

      {filtros?.habilitados && (
        <Box style={{ flexShrink: 0 }}>
          <FiltroLogisticaVista
            filtro={filtros.filtro}
            catalogos={catalogos}
            alCambiarFiltro={filtros.alCambiarFiltro}
            alConsultar={filtros.alConsultar}
            alLimpiar={filtros.alLimpiar}
          />
        </Box>
      )}

      <div style={{ flex: 1, minHeight: 400 }}>
        <Tabla tabla={tabla} modoEdicion={true} filaEnEdicion={filaEnEdicion} />
      </div>
    </div>
  );
};
